/**
 * Pool Settings — the module's admin screen. `_manage`-gated end to end (pool role owner or admin; a
 * pool-Viewer never sees the tab and a direct-URL hit renders the generic RBAC-forbidden state below,
 * not <PoolErrorState/> — see the page-level gate at the bottom of this file): once past that gate every
 * section on this screen is fully interactive, there is no further per-button read-only dance the way
 * PeriodModal has one (rev 6 R2 made Settings binary — owner/admin can edit everything here, a viewer
 * can't reach the screen at all).
 *
 *  • "Add a pool account" — rev 7 (21/7, from a live prod screenshot review): a single "Add Account"
 *    button opens <AddAccountModal/> (features/pool/AddAccountModal.tsx), which owns the whole two-step
 *    flow that used to live inline here as an always-visible two-tab section — choose existing-account
 *    vs. Zoom-credential, then the matching form. See that file for the two modes (rev 6 R4): an
 *    existing ZPCD-mirrored sub-account (search by name/number, the original Phase 1 flow), or standalone
 *    from a Zoom S2S credential — the real case a mirror search can't reach (5003399647, 86 paying
 *    customers, never landed in the mirror).
 *  • the current pool accounts, each with an enable/disable toggle (disable FREEZES the pool — sweep
 *    stops touching it; it is never a delete, and the confirm dialog says so) and a Remove action (rev 7,
 *    21/7 — <RemovePoolAccountDialog/>, features/pool/RemovePoolAccountDialog.tsx) that actually drops
 *    the pool row and its observed membership cache, keeping service periods + audit history.
 *  • rev 7 (21/7) ALSO removed the Managed domain column + its Recheck row action from this table (a
 *    live prod screenshot review) — see ExistingPoolAccountsSection's own comment on why the underlying
 *    guard is unaffected.
 *  • Pool operator access (rev 6 R1/R3, rev 8 R1 corrected flow 21/7) — the portal's Owner(s) are ALWAYS
 *    pinned at the top of this list with an "Owner" badge, not editable or removable (there is no
 *    underlying row to revoke: their access is implicit). Below them, granted `pool.operator` rows
 *    (Admin/Viewer) can be revoked. Granting a new one is a single "Add User" button opening
 *    <AddUserModal/> (features/pool/AddUserModal.tsx) — which owns the whole select-THEN-role flow that
 *    used to live inline here as a search table + a per-row role Select (the old layout let a role be
 *    picked before a user was even selected; the modal fixes the ordering).
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { ApiRequestError } from "@/api/client";
import {
  Button,
  EmptyState,
  ErrorState,
  Modal,
  SkeletonRows,
  StatusPill,
  Table,
  Badge,
  useNotifier,
  type Column,
} from "@/components/ui";
import { AddAccountModal } from "@/features/pool/AddAccountModal";
import { AddUserModal } from "@/features/pool/AddUserModal";
import { ChangeRoleModal } from "@/features/pool/ChangeRoleModal";
import { isNotPoolOperator, PoolErrorState } from "@/features/pool/PoolErrorState";
import { RemovePoolAccountDialog } from "@/features/pool/RemovePoolAccountDialog";
import { StandaloneAccountDialog } from "@/features/pool/StandaloneAccountDialog";
import {
  usePoolAccess,
  usePoolAccounts,
  usePoolOperators,
  useRemovePoolOperator,
  useSetPoolAccountEnabled,
} from "@/features/pool/hooks";
import { useRbac } from "@/context/RbacContext";
import { formatInstant } from "@/lib/format";
import type { PoolAccount, PoolOperatorRole, PoolOperatorRow } from "@/api/types";
import type { TKey } from "@/i18n/keys";

function errMessage(e: unknown, fallback: string): string {
  return e instanceof ApiRequestError ? e.error.message : fallback;
}

/** account_id is the only field guaranteed non-null, so it's the final fallback — every {{account}}
 *  interpolation below needs a real `string`, never `string | null`. */
function accountLabel(a: {
  account_name: string | null;
  account_number: string | null;
  account_id: string;
}): string {
  return a.account_name ?? a.account_number ?? a.account_id;
}

/** Statically-known enum → i18n key, compile-checked (same pattern as PoolSystemPage's STATE_LABEL_KEY) —
 *  reuses the portal's existing `role.*` copy rather than inventing a second "Admin"/"Viewer" wording. */
const OPERATOR_ROLE_LABEL_KEY: Record<PoolOperatorRole, TKey> = {
  admin: "role.admin",
  viewer: "role.viewer",
};

export function PoolSettingsPage() {
  const { t } = useTranslation();
  const accessQ = usePoolAccess();

  if (accessQ.isPending) return <SkeletonRows rows={6} />;
  if (accessQ.isError) {
    return <PoolErrorState error={accessQ.error} onRetry={() => void accessQ.refetch()} />;
  }

  // Binary, rev 6 R2: this whole screen is owner/admin-only — no read-only "viewer looks but can't
  // edit" state the way PeriodModal has one (viewer never gets a Settings tab in the first place; a
  // direct URL hit lands here and gets the app's normal forbidden copy, not the pool-operator one).
  const canManage = accessQ.data.role === "owner" || accessQ.data.role === "admin";
  if (!canManage) {
    return <ErrorState title={t("state.forbiddenTitle")} body={t("state.forbiddenBody")} />;
  }

  return <PoolSettingsContent />;
}

function PoolSettingsContent() {
  const { t } = useTranslation();
  const accountsQ = usePoolAccounts();
  const [addModalOpen, setAddModalOpen] = useState(false);

  return (
    <section aria-labelledby="pool-settings-title" className="flex flex-col gap-8">
      <header className="flex flex-col gap-1.5">
        <h1
          id="pool-settings-title"
          className="text-(length:--text-2xl) font-semibold text-(--text-strong)"
        >
          {t("pool.settings.title")}
        </h1>
        <p className="text-(length:--text-sm) text-(--text-muted)">{t("pool.settings.subtitle")}</p>
      </header>

      {accountsQ.isPending ? (
        <SkeletonRows rows={5} />
      ) : accountsQ.isError ? (
        <PoolErrorState error={accountsQ.error} onRetry={() => void accountsQ.refetch()} />
      ) : (
        <>
          <AddPoolAccountSection onOpenAddModal={() => setAddModalOpen(true)} />
          <ExistingPoolAccountsSection accounts={accountsQ.data.accounts} />
        </>
      )}

      <PoolOperatorsSection />

      {addModalOpen && <AddAccountModal onClose={() => setAddModalOpen(false)} />}
    </section>
  );
}

// --- Add a pool account: a single button opening <AddAccountModal/> (rev 7) ----------------------------

function AddPoolAccountSection({ onOpenAddModal }: { onOpenAddModal: () => void }) {
  const { t } = useTranslation();

  return (
    <section className="flex items-center justify-between gap-3">
      <h2 className="text-(length:--text-lg) font-semibold text-(--text-strong)">
        {t("pool.settings.addAccountTitle")}
      </h2>
      <Button onClick={onOpenAddModal}>
        <Plus className="size-4" aria-hidden />
        {t("pool.settings.addAccountButton")}
      </Button>
    </section>
  );
}

// --- Current pool accounts ------------------------------------------------------------------------

function ExistingPoolAccountsSection({ accounts }: { accounts: PoolAccount[] }) {
  const { t } = useTranslation();
  const { notify } = useNotifier();
  const enableMut = useSetPoolAccountEnabled();
  const [confirmTarget, setConfirmTarget] = useState<PoolAccount | null>(null);
  const [removeTarget, setRemoveTarget] = useState<PoolAccount | null>(null);
  const [standaloneTarget, setStandaloneTarget] = useState<PoolAccount | null>(null);
  // In-box error (role="alert"), same pattern as PoolSystemPage's confirm dialogs — a toast fired from
  // a native-<dialog> onError sinks BEHIND the modal backdrop, so the operator never saw the refusal.
  const [submitError, setSubmitError] = useState<string | null>(null);

  function closeConfirm() {
    setConfirmTarget(null);
    setSubmitError(null);
  }

  const columns: Column<PoolAccount>[] = [
    {
      key: "name",
      header: t("pool.settings.colAccountName"),
      sortValue: (r) => r.account_name ?? "",
      cell: (r) => (
        <span className="flex flex-col leading-tight">
          <span className="font-medium text-(--text)">{r.account_name ?? "—"}</span>
          <span className="text-(length:--text-2xs) text-(--text-muted)">
            {r.account_number ?? "—"}
          </span>
        </span>
      ),
    },
    {
      key: "enabled",
      header: t("pool.settings.colEnabled"),
      sortValue: (r) => (r.enabled ? 1 : 0),
      cell: (r) => (
        <StatusPill tone={r.enabled ? "ok" : "neutral"}>
          {r.enabled ? t("pool.settings.enabledOn") : t("pool.settings.enabledOff")}
        </StatusPill>
      ),
    },
    // rev 7 (21/7) removed the Managed domain column + its Recheck row action from this table. rev 9
    // (21/7, principal decision) then removed the underlying concept entirely — ManagedDomainBadge.tsx,
    // has_managed_domain/managed_domains_checked_at, the recheck-domains endpoint, and the force-unlink
    // gate they fed are all gone (see PeriodModal.tsx's own banner) — so there is no longer any
    // "underlying protection" this table's missing column stands in front of.
    {
      key: "actions",
      header: t("pool.settings.colActions"),
      align: "right",
      cell: (r) => (
        <span className="flex justify-end gap-2">
          {/* rev 15 — standalone (credential) accounts get a Manage action for the two things a
              per-account S2S app can't get from the mirror: re-test the credential + enter the
              Zoom subscription end by hand. Master accounts never show it (both come from ZPCD). */}
          {!r.under_sub_account && (
            <Button size="sm" variant="secondary" onClick={() => setStandaloneTarget(r)}>
              {t("pool.settings.standalone.manageButton")}
            </Button>
          )}
          <Button size="sm" variant="secondary" onClick={() => setConfirmTarget(r)}>
            {r.enabled ? t("pool.settings.disableButton") : t("pool.settings.enableButton")}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="text-(--danger)"
            onClick={() => setRemoveTarget(r)}
          >
            {t("pool.settings.removeButton")}
          </Button>
        </span>
      ),
    },
  ];

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-(length:--text-lg) font-semibold text-(--text-strong)">
        {t("pool.settings.existingTitle")}
      </h2>
      <Table
        columns={columns}
        rows={accounts}
        rowKey={(r) => r.account_id}
        caption={t("pool.settings.existingTitle")}
        initialSort={{ key: "name", dir: "asc" }}
        empty={
          <EmptyState
            title={t("pool.settings.existingEmptyTitle")}
            body={t("pool.settings.existingEmptyBody")}
          />
        }
      />

      {confirmTarget && (
        <Modal
          open
          onClose={closeConfirm}
          title={
            confirmTarget.enabled
              ? t("pool.settings.disableDialogTitle")
              : t("pool.settings.enableDialogTitle")
          }
          size="sm"
          footer={
            <>
              <Button variant="secondary" onClick={closeConfirm}>
                {t("action.cancel")}
              </Button>
              <Button
                variant={confirmTarget.enabled ? "danger" : "primary"}
                loading={enableMut.isPending}
                onClick={() => {
                  setSubmitError(null);
                  enableMut.mutate(
                    { accountId: confirmTarget.account_id, enabled: !confirmTarget.enabled },
                    {
                      onSuccess: () => {
                        notify({
                          tone: "ok",
                          title: t(
                            confirmTarget.enabled
                              ? "pool.settings.disableSuccess"
                              : "pool.settings.enableSuccess",
                            { account: accountLabel(confirmTarget) },
                          ),
                        });
                        closeConfirm();
                      },
                      // In-box, not a toast: the modal stays open so the refusal is readable in it.
                      onError: (e) => setSubmitError(errMessage(e, t("state.errorTitle"))),
                    },
                  );
                }}
              >
                {confirmTarget.enabled
                  ? t("pool.settings.disableButton")
                  : t("pool.settings.enableButton")}
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-3">
            <p className="text-(length:--text-sm) text-(--text)">
              {t(
                confirmTarget.enabled
                  ? "pool.settings.disableDialogBody"
                  : "pool.settings.enableDialogBody",
                { account: accountLabel(confirmTarget) },
              )}
            </p>
            {submitError && (
              <p role="alert" className="text-(length:--text-sm) text-(--danger)">
                {submitError}
              </p>
            )}
          </div>
        </Modal>
      )}

      {removeTarget && (
        <RemovePoolAccountDialog
          account={removeTarget}
          onRemoved={(membersRemoved) =>
            notify({
              tone: "ok",
              title: t("pool.settings.removeSuccess", {
                account: accountLabel(removeTarget),
                members: membersRemoved,
              }),
            })
          }
          onClose={() => setRemoveTarget(null)}
        />
      )}

      {standaloneTarget && (
        <StandaloneAccountDialog
          account={standaloneTarget}
          onClose={() => setStandaloneTarget(null)}
        />
      )}
    </section>
  );
}

// --- Pool operator access: Owner(s) pinned + grantable Admin/Viewer rows (rev 6 R1/R3) ---------------

type OperatorListRow = { kind: "owner"; email: string } | ({ kind: "operator" } & PoolOperatorRow);

/** Hidden entirely on a 403 (an edge case here — the page-level gate above already requires owner/admin
 *  before this section ever mounts) — never rendered as a disabled/greyed section. */
function PoolOperatorsSection() {
  const { t } = useTranslation();
  const { notify } = useNotifier();
  const { me } = useRbac();
  const operatorsQ = usePoolOperators();
  const removeMut = useRemovePoolOperator();
  const [revokeTarget, setRevokeTarget] = useState<PoolOperatorRow | null>(null);
  const [changeRoleTarget, setChangeRoleTarget] = useState<PoolOperatorRow | null>(null);
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);
  // In-box error, same reasoning as the enable/disable dialog above (a toast sinks behind the backdrop).
  const [submitError, setSubmitError] = useState<string | null>(null);
  // 30/7 — the signed-in staffer's OWN row: Change role AND Revoke both hide on it (the server is about
  // to start rejecting a self-change/self-revoke with 422 either way — see this section's own Actions
  // cell below). Normalized lower-case both sides: `r.email` off the wire is already lower-cased
  // (server-side), but `me.email` (Auth0's own claim, or a locally-typed portal invite) is not
  // guaranteed to be.
  const selfEmail = me.email.toLowerCase();

  function closeRevoke() {
    setRevokeTarget(null);
    setSubmitError(null);
  }

  if (isNotPoolOperator(operatorsQ.error)) return null;
  if (operatorsQ.isPending) return <SkeletonRows rows={2} />;
  if (operatorsQ.isError) {
    return <PoolErrorState error={operatorsQ.error} onRetry={() => void operatorsQ.refetch()} />;
  }

  const rows: OperatorListRow[] = [
    ...operatorsQ.data.owners.map((o) => ({ kind: "owner" as const, email: o.email })),
    ...operatorsQ.data.operators.map((o) => ({ kind: "operator" as const, ...o })),
  ];

  const columns: Column<OperatorListRow>[] = [
    {
      key: "email",
      header: t("pool.settings.colOperatorEmail"),
      sortValue: (r) => r.email,
      cell: (r) => r.email,
    },
    {
      key: "role",
      header: t("pool.settings.colRole"),
      cell: (r) =>
        r.kind === "owner" ? (
          <Badge tone="brand">{t("pool.settings.ownerBadge")}</Badge>
        ) : (
          <StatusPill tone={r.role === "admin" ? "ok" : "neutral"}>
            {t(OPERATOR_ROLE_LABEL_KEY[r.role])}
          </StatusPill>
        ),
    },
    {
      key: "grantedBy",
      header: t("pool.settings.colGrantedBy"),
      cell: (r) => (r.kind === "operator" ? r.granted_by : "—"),
    },
    {
      key: "grantedAt",
      header: t("pool.settings.colGrantedAt"),
      numeric: true,
      sortValue: (r) => (r.kind === "operator" ? r.granted_at : ""),
      cell: (r) => (r.kind === "operator" ? formatInstant(r.granted_at) : "—"),
    },
    {
      key: "actions",
      header: t("pool.settings.colActions"),
      align: "right",
      cell: (r) => {
        // Owner rows never get an action (rev 6 R1 — no underlying row to change). The signed-in
        // staffer's OWN operator row also gets neither: Change role AND Revoke both hide (30/7 — the
        // server is about to start rejecting a self-change/self-revoke with 422 either way, and an
        // action an operator can click but that will always refuse is worse than no action at all).
        if (r.kind !== "operator" || r.email.toLowerCase() === selfEmail) return null;
        return (
          <span className="flex justify-end gap-2">
            <Button size="sm" variant="secondary" onClick={() => setChangeRoleTarget(r)}>
              {t("pool.settings.changeRoleButton")}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-(--danger)"
              onClick={() => setRevokeTarget(r)}
            >
              {t("pool.settings.revokeButton")}
            </Button>
          </span>
        );
      },
    },
  ];

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h2 className="text-(length:--text-lg) font-semibold text-(--text-strong)">
            {t("pool.settings.operatorsTitle")}
          </h2>
          <p className="text-(length:--text-xs) text-(--text-muted)">
            {t("pool.settings.operatorsSubtitle")}
          </p>
        </div>
        <Button onClick={() => setAddUserModalOpen(true)}>
          <Plus className="size-4" aria-hidden />
          {t("pool.settings.addUserButton")}
        </Button>
      </div>

      <Table
        columns={columns}
        rows={rows}
        rowKey={(r) => r.email}
        caption={t("pool.settings.operatorsTitle")}
        paginated={false}
        initialSort={{ key: "email", dir: "asc" }}
        // The Owner row(s) stay pinned above any sort — there is nothing to sort them against; they are
        // always the top of this list (rev 6 R1).
        pinFirst={(r) => r.kind === "owner"}
        empty={
          <EmptyState
            title={t("pool.settings.operatorsEmptyTitle")}
            body={t("pool.settings.operatorsEmptyBody")}
          />
        }
      />

      {addUserModalOpen && <AddUserModal onClose={() => setAddUserModalOpen(false)} />}

      {changeRoleTarget && (
        <ChangeRoleModal operator={changeRoleTarget} onClose={() => setChangeRoleTarget(null)} />
      )}

      {revokeTarget && (
        <Modal
          open
          onClose={closeRevoke}
          title={t("pool.settings.revokeDialogTitle")}
          size="sm"
          footer={
            <>
              <Button variant="secondary" onClick={closeRevoke}>
                {t("action.cancel")}
              </Button>
              <Button
                variant="danger"
                loading={removeMut.isPending}
                onClick={() => {
                  setSubmitError(null);
                  removeMut.mutate(revokeTarget.email, {
                    onSuccess: () => {
                      notify({
                        tone: "ok",
                        title: t("pool.settings.revokeSuccess", { email: revokeTarget.email }),
                      });
                      closeRevoke();
                    },
                    // In-box, not a toast: the modal stays open so the refusal is readable in it.
                    onError: (e) => setSubmitError(errMessage(e, t("state.errorTitle"))),
                  });
                }}
              >
                {t("pool.settings.revokeButton")}
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-3">
            <p className="text-(length:--text-sm) text-(--text)">
              {t("pool.settings.revokeDialogBody", { email: revokeTarget.email })}
            </p>
            {submitError && (
              <p role="alert" className="text-(length:--text-sm) text-(--danger)">
                {submitError}
              </p>
            )}
          </div>
        </Modal>
      )}
    </section>
  );
}
